import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eye, History, Layers, Pencil, Plus, RefreshCw, Search } from 'lucide-react';
import { dynamicFieldsApi } from './api';
import LibraryPreviewDialog from './LibraryPreviewDialog';
import { LibrarySummary } from './types';
import { Chip, timeAgo } from '../components/history';
import { inputClass, primaryButton, secondaryButton, Skeleton } from '../ui';
import { ROUTES } from '../../../config/links';
import { TourButton } from '../tour/TourButton';

/** Every sensor type + category with the dynamic fields forms can select from. */
export default function DynamicFieldsListPage() {
  const navigate = useNavigate();
  const [libraries, setLibraries] = useState<LibrarySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [previewing, setPreviewing] = useState<LibrarySummary>();

  const load = () => {
    setLoading(true);
    dynamicFieldsApi.libraries()
      .then(setLibraries)
      .catch((e) => toast.error(e?.response?.data?.detail || e?.message || 'Failed to load dynamic field libraries'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? libraries.filter((l) => `${l.sensor_type} ${l.category}`.toLowerCase().includes(needle)) : libraries;
  }, [libraries, q]);

  return (
    <div className="min-h-full bg-slate-50 p-3 md:p-8">
      <header className="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-end sm:justify-between" data-tour="dynamic-fields-header">
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600"><Layers className="h-3.5 w-3.5" /> Dynamic fields</div>
          <h1 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">Dynamic field libraries</h1>
          <p className="mt-1 max-w-2xl text-xs text-slate-500">Define the runtime fields (for example frequency and temperature readings) once for each sensor type and category. Form builders then pick from this library, so the fields a form will show are known up front. Every change is versioned and audited.</p>
        </div>
        <div className="flex gap-2">
          <TourButton id="dynamicList" ready={!loading} />
          <label className="relative" data-tour="dynamic-fields-search">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input className={`${inputClass()} !mt-0 h-9 w-56 pl-8`} placeholder="Search sensor type or category" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search libraries" />
          </label>
          <button className={secondaryButton} onClick={load} disabled={loading}><RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh</button>
        </div>
      </header>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[720px] text-left text-[12px]">
          <thead className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Sensor type</th>
              <th className="px-4 py-2.5">Category</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5">Entries</th>
              <th className="px-4 py-2.5">Last change</th>
              <th className="px-4 py-2.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && libraries.length === 0 && Array.from({ length: 5 }).map((_, i) => (
              <tr key={i} aria-hidden="true">{Array.from({ length: 6 }).map((__, j) => <td key={j} className="px-4 py-3"><Skeleton className="h-4 w-24" /></td>)}</tr>
            ))}
            {!loading && visible.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No sensor types match.</td></tr>}
            {visible.map((l, rowIndex) => (
              <tr key={`${l.sensor_type}|${l.category}`} className="hover:bg-slate-50/60" data-tour={rowIndex === 0 ? 'dynamic-fields-row' : undefined}>
                <td className="px-4 py-3 font-medium text-slate-800">{l.sensor_type}</td>
                <td className="px-4 py-3 text-slate-700">{l.category}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    {l.published_version != null ? <Chip cls="bg-emerald-100 text-emerald-700">Live v{l.published_version}</Chip> : <Chip cls="bg-slate-100 text-slate-500">Not published</Chip>}
                    {l.has_draft && <Chip cls="bg-amber-100 text-amber-700">Draft</Chip>}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-600">{l.published_version != null ? l.entry_count : '—'}</td>
                <td className="px-4 py-3 text-slate-500">{l.updated_at ? <>{timeAgo(l.updated_at)}{l.updated_by ? ` · ${l.updated_by}` : ''}</> : '—'}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1.5" data-tour={rowIndex === 0 ? 'dynamic-fields-actions' : undefined}>
                    <button className={secondaryButton} onClick={() => setPreviewing(l)} disabled={l.published_version == null} title={l.published_version == null ? 'Publish the library to preview it' : 'See the fields a form receives, without editing'}>
                      <Eye className="h-3.5 w-3.5" /> Preview
                    </button>
                    <button className={primaryButton} onClick={() => navigate(ROUTES.dynamicFieldsEdit(l.sensor_type, l.category))}>
                      {l.published_version == null && !l.has_draft ? <><Plus className="h-3.5 w-3.5" /> Define</> : <><Pencil className="h-3.5 w-3.5" /> Edit</>}
                    </button>
                    {(l.published_version != null || l.has_draft) && (
                      <>
                        <button className={secondaryButton} onClick={() => navigate(ROUTES.dynamicFieldsVersions(l.sensor_type, l.category))}><Layers className="h-3.5 w-3.5" /> Versions</button>
                        <button className={secondaryButton} onClick={() => navigate(ROUTES.dynamicFieldsAudit(l.sensor_type, l.category))}><History className="h-3.5 w-3.5" /> Audit</button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {previewing && (
        <LibraryPreviewDialog
          library={{ sensor_type: previewing.sensor_type, category: previewing.category }}
          version={previewing.published_version}
          onClose={() => setPreviewing(undefined)}
        />
      )}
    </div>
  );
}
